/**
 * Dashboard Store (Zustand)
 *
 * Centralized dashboard state management with localStorage persistence for view mode.
 * Manages document list, pagination, sorting, search, and view mode.
 *
 * @module features/dashboard/store/dashboardStore
 * @see SPEC-016 Requirements 1.1, 6.1, 6.2
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ApiError, ApiErrorCode } from '@linkode/shared';
import type { DocListItemInput, ListDocsQueryInput } from '@linkode/shared';
import { dashboardApiClient } from '../api/dashboardApiClient';
import { useAuthStore } from '../../auth/store/authStore';

// ============================================================================
// Constants
// ============================================================================

/** localStorage key for persisted dashboard state */
const STORAGE_KEY = 'linkode-dashboard';

/** Default page size for document list */
const DEFAULT_PAGE_SIZE = 50;

// ============================================================================
// Types
// ============================================================================

/**
 * View mode options for the dashboard
 * @see Requirement 6.1
 */
export type ViewMode = 'list' | 'grid' | 'flow';

/**
 * Sort field options
 */
export type SortBy = 'updatedAt' | 'createdAt' | 'title';

/**
 * Sort order options
 */
export type SortOrder = 'asc' | 'desc';

// ============================================================================
// State Interface
// ============================================================================

/**
 * Dashboard state interface
 *
 * @see Requirements 1.1, 6.1, 6.2
 */
export interface DashboardState {
  // ─────────────────────────────────────────────────────────────────────────
  // Document List State
  // ─────────────────────────────────────────────────────────────────────────

  /** List of documents for current view */
  docs: DocListItemInput[];

  /** Total number of documents matching current filters */
  total: number;

  /** Whether there are more documents to load */
  hasMore: boolean;

  /** Loading state for document list */
  isLoading: boolean;

  /** Error from last operation */
  error: ApiError | null;

  // ─────────────────────────────────────────────────────────────────────────
  // Filter & Sort State
  // ─────────────────────────────────────────────────────────────────────────

  /** Current folder ID filter (null for root) */
  currentFolderId: string | null;

  /** Current sort field */
  sortBy: SortBy;

  /** Current sort order */
  sortOrder: SortOrder;

  /** Current search query */
  searchQuery: string;

  // ─────────────────────────────────────────────────────────────────────────
  // Pagination State
  // ─────────────────────────────────────────────────────────────────────────

  /** Current page offset */
  offset: number;

  /** Page size limit */
  limit: number;

  // ─────────────────────────────────────────────────────────────────────────
  // UI State
  // ─────────────────────────────────────────────────────────────────────────

  /** Current view mode (persisted to localStorage) */
  viewMode: ViewMode;

  /** Selected document IDs for bulk operations */
  selectedDocIds: string[];

  /** Whether Zustand persist has completed hydration */
  _hasHydrated: boolean;

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Fetch documents with current filters
   * @see Requirement 1.1
   */
  fetchDocs: () => Promise<void>;

  /**
   * Load more documents (pagination)
   * @see Requirement 1.5
   */
  loadMore: () => Promise<void>;

  /**
   * Refresh document list (reset pagination)
   */
  refresh: () => Promise<void>;

  /**
   * Set current folder filter
   * @param folderId - Folder ID or null for root
   */
  setCurrentFolder: (folderId: string | null) => void;

  /**
   * Set sort field
   * @param sortBy - Sort field
   * @see Requirement 1.3
   */
  setSortBy: (sortBy: SortBy) => void;

  /**
   * Set sort order
   * @param sortOrder - Sort order
   * @see Requirement 1.4
   */
  setSortOrder: (sortOrder: SortOrder) => void;

  /**
   * Set search query
   * @param query - Search query string
   * @see Requirement 8.1
   */
  setSearchQuery: (query: string) => void;

  /**
   * Set view mode (persisted to localStorage)
   * @param mode - View mode
   * @see Requirements 6.1, 6.2
   */
  setViewMode: (mode: ViewMode) => void;

  /**
   * Toggle document selection
   * @param docId - Document ID to toggle
   */
  toggleDocSelection: (docId: string) => void;

  /**
   * Select all documents
   */
  selectAll: () => void;

  /**
   * Clear all selections
   */
  clearSelection: () => void;

  /**
   * Delete a document (soft delete)
   * @param docId - Document ID to delete
   * @see Requirement 7.4
   */
  deleteDoc: (docId: string) => Promise<void>;

  /**
   * Move a document to a folder
   * @param docId - Document ID to move
   * @param targetFolderPath - Target folder path
   * @see Requirement 2.4
   */
  moveDoc: (docId: string, targetFolderPath: string) => Promise<void>;

  /**
   * Set error state
   * @param error - ApiError or null
   */
  setError: (error: ApiError | null) => void;

  /**
   * Clear error state
   */
  clearError: () => void;

  /**
   * Set hydration state
   * @internal
   */
  setHasHydrated: (state: boolean) => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialState = {
  docs: [] as DocListItemInput[],
  total: 0,
  hasMore: false,
  isLoading: false,
  error: null,
  currentFolderId: null,
  sortBy: 'updatedAt' as SortBy,
  sortOrder: 'desc' as SortOrder,
  searchQuery: '',
  offset: 0,
  limit: DEFAULT_PAGE_SIZE,
  viewMode: 'list' as ViewMode,
  selectedDocIds: [] as string[],
  _hasHydrated: false,
};

// ============================================================================
// Store Creation
// ============================================================================

/**
 * Dashboard Store
 *
 * Zustand store for dashboard state management with localStorage persistence.
 *
 * Features:
 * - Document list with pagination
 * - Sorting and filtering
 * - Search functionality
 * - View mode persistence
 * - Bulk selection
 *
 * @example
 * ```tsx
 * // Get documents
 * const docs = useDashboardStore((state) => state.docs);
 *
 * // Fetch documents
 * const fetchDocs = useDashboardStore((state) => state.fetchDocs);
 * useEffect(() => { fetchDocs(); }, [fetchDocs]);
 *
 * // Change view mode
 * const setViewMode = useDashboardStore((state) => state.setViewMode);
 * setViewMode('grid');
 *
 * // Search
 * const setSearchQuery = useDashboardStore((state) => state.setSearchQuery);
 * setSearchQuery('my document');
 * ```
 *
 * @see Requirements 1.1, 6.1, 6.2
 */
export const useDashboardStore = create<DashboardState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // ─────────────────────────────────────────────────────────────────────
      // Actions
      // ─────────────────────────────────────────────────────────────────────

      fetchDocs: async () => {
        const { currentFolderId, sortBy, sortOrder, searchQuery, limit, isLoading } = get();

        // Prevent duplicate fetches - if already loading, skip
        if (isLoading) {
          return;
        }

        set({ isLoading: true, error: null });

        try {
          const query: Partial<ListDocsQueryInput> = {
            sortBy,
            sortOrder,
            limit,
            offset: 0,
          };

          if (currentFolderId) {
            query.folderId = currentFolderId;
          }

          if (searchQuery.trim()) {
            query.search = searchQuery.trim();
          }

          const response = await dashboardApiClient.listDocs(query);

          set({
            docs: response.docs,
            total: response.total,
            hasMore: response.hasMore,
            offset: 0,
            isLoading: false,
          });
        } catch (error) {
          const apiError = error as ApiError;
          // Handle UNAUTHORIZED - clear auth state and let AuthGuard redirect
          // Use setTimeout(0) to defer logout to next event loop tick, preventing
          // React Error #185 (Maximum update depth exceeded) by allowing
          // current render cycle to complete before triggering auth state change
          if (apiError.code === ApiErrorCode.UNAUTHORIZED) {
            set({ isLoading: false });
            // Defer logout to next event loop tick to avoid state update during render
            setTimeout(() => {
              useAuthStore.getState().logout();
            }, 0);
            return;
          }
          set({
            error: apiError,
            isLoading: false,
          });
        }
      },

      loadMore: async () => {
        const {
          currentFolderId,
          sortBy,
          sortOrder,
          searchQuery,
          limit,
          offset,
          docs,
          hasMore,
          isLoading,
        } = get();

        if (!hasMore || isLoading) return;

        set({ isLoading: true, error: null });

        try {
          const newOffset = offset + limit;
          const query: Partial<ListDocsQueryInput> = {
            sortBy,
            sortOrder,
            limit,
            offset: newOffset,
          };

          if (currentFolderId) {
            query.folderId = currentFolderId;
          }

          if (searchQuery.trim()) {
            query.search = searchQuery.trim();
          }

          const response = await dashboardApiClient.listDocs(query);

          set({
            docs: [...docs, ...response.docs],
            total: response.total,
            hasMore: response.hasMore,
            offset: newOffset,
            isLoading: false,
          });
        } catch (error) {
          const apiError = error as ApiError;
          // Handle UNAUTHORIZED - clear auth state and let AuthGuard redirect
          // Use setTimeout(0) to defer logout to next event loop tick, preventing
          // React Error #185 (Maximum update depth exceeded) by allowing
          // current render cycle to complete before triggering auth state change
          if (apiError.code === ApiErrorCode.UNAUTHORIZED) {
            set({ isLoading: false });
            // Defer logout to next event loop tick to avoid state update during render
            setTimeout(() => {
              useAuthStore.getState().logout();
            }, 0);
            return;
          }
          set({
            error: apiError,
            isLoading: false,
          });
        }
      },

      refresh: async () => {
        set({ offset: 0, docs: [], selectedDocIds: [] });
        await get().fetchDocs();
      },

      setCurrentFolder: (folderId) => {
        set({ currentFolderId: folderId, offset: 0, docs: [], selectedDocIds: [] });
        get().fetchDocs();
      },

      setSortBy: (sortBy) => {
        set({ sortBy, offset: 0, docs: [] });
        get().fetchDocs();
      },

      setSortOrder: (sortOrder) => {
        set({ sortOrder, offset: 0, docs: [] });
        get().fetchDocs();
      },

      setSearchQuery: (searchQuery) => {
        set({ searchQuery, offset: 0, docs: [] });
        // Note: Debouncing should be handled by the component
        get().fetchDocs();
      },

      setViewMode: (viewMode) => {
        set({ viewMode });
      },

      toggleDocSelection: (docId) => {
        const { selectedDocIds } = get();
        const isSelected = selectedDocIds.includes(docId);
        set({
          selectedDocIds: isSelected
            ? selectedDocIds.filter((id) => id !== docId)
            : [...selectedDocIds, docId],
        });
      },

      selectAll: () => {
        const { docs } = get();
        set({ selectedDocIds: docs.map((doc) => doc.docId) });
      },

      clearSelection: () => {
        set({ selectedDocIds: [] });
      },

      deleteDoc: async (docId) => {
        set({ isLoading: true, error: null });

        try {
          await dashboardApiClient.deleteDoc(docId);

          // Remove from local state
          const { docs, selectedDocIds, total } = get();
          set({
            docs: docs.filter((doc) => doc.docId !== docId),
            selectedDocIds: selectedDocIds.filter((id) => id !== docId),
            total: total - 1,
            isLoading: false,
          });
        } catch (error) {
          set({
            error: error as ApiError,
            isLoading: false,
          });
          throw error;
        }
      },

      moveDoc: async (docId, targetFolderPath) => {
        set({ isLoading: true, error: null });

        try {
          await dashboardApiClient.moveDoc(docId, { targetFolderPath });

          // Refresh to get updated list
          await get().refresh();
        } catch (error) {
          set({
            error: error as ApiError,
            isLoading: false,
          });
          throw error;
        }
      },

      setError: (error) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      setHasHydrated: (state) => {
        set({ _hasHydrated: state });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Only persist view mode preference
      partialize: (state) => ({
        viewMode: state.viewMode,
      }),
      // Called when hydration is complete
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

/**
 * Select documents list
 */
export const selectDocs = (state: DashboardState): DocListItemInput[] => state.docs;

/**
 * Select total document count
 */
export const selectTotal = (state: DashboardState): number => state.total;

/**
 * Select hasMore flag
 */
export const selectHasMore = (state: DashboardState): boolean => state.hasMore;

/**
 * Select loading state
 */
export const selectIsLoading = (state: DashboardState): boolean => state.isLoading;

/**
 * Select error state
 */
export const selectError = (state: DashboardState): ApiError | null => state.error;

/**
 * Select current folder ID
 */
export const selectCurrentFolderId = (state: DashboardState): string | null =>
  state.currentFolderId;

/**
 * Select sort field
 */
export const selectSortBy = (state: DashboardState): SortBy => state.sortBy;

/**
 * Select sort order
 */
export const selectSortOrder = (state: DashboardState): SortOrder => state.sortOrder;

/**
 * Select search query
 */
export const selectSearchQuery = (state: DashboardState): string => state.searchQuery;

/**
 * Select view mode
 * @see Requirements 6.1, 6.2
 */
export const selectViewMode = (state: DashboardState): ViewMode => state.viewMode;

/**
 * Select selected document IDs
 */
export const selectSelectedDocIds = (state: DashboardState): string[] => state.selectedDocIds;

/**
 * Select hydration state
 */
export const selectHasHydrated = (state: DashboardState): boolean => state._hasHydrated;

// ============================================================================
// View Mode Persistence Utilities
// ============================================================================

/**
 * Get persisted view mode from localStorage
 * Useful for SSR or initial render before hydration
 *
 * @returns The persisted view mode or default 'list'
 * @see Requirements 6.1, 6.2
 */
export function getPersistedViewMode(): ViewMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.state?.viewMode) {
        return parsed.state.viewMode as ViewMode;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return 'list';
}

/**
 * Set view mode directly to localStorage
 * Useful for testing or external updates
 *
 * @param mode - View mode to persist
 * @see Requirements 6.1, 6.2
 */
export function setPersistedViewMode(mode: ViewMode): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : { state: {} };
    parsed.state = { ...parsed.state, viewMode: mode };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(parsed));
  } catch {
    // Ignore errors
  }
}
